import type { Appointment } from 'pos-client/components/customScheduler/types/schedulerTypes';

import {
  type AppointmentLayout,
  calculateAppointmentLayouts,
} from './appointmentLayoutUtils.ts';

/**
 * Per-lane, per-appointment layout map produced by
 * {@link computeLaneAppointmentLayouts}.
 *
 * Outer key: `${laneId}-${appointmentId}`
 * Inner key: `appointmentId`
 */
export type LaneAppointmentLayouts = Map<
  string,
  Map<string | number, AppointmentLayout>
>;

interface Options {
  maxVisibleColumns?: number;
}

const getAppointmentIdString = (app: Appointment): string =>
  String(
    app.jobId ||
      app.scheduleId ||
      app.bookingReference ||
      `${app.startDate}-${app.endDate}-${app.text}`
  );

const getEffectiveStart = (app: Appointment): string =>
  typeof app.startDateDisplay === 'string'
    ? app.startDateDisplay
    : app.startDate;

const getEffectiveEnd = (app: Appointment): string =>
  typeof app.endDateDisplay === 'string' ? app.endDateDisplay : app.endDate;

export function computeLaneAppointmentLayouts(
  appointmentsByLane: Record<string, Appointment[]>,
  options: Options = {}
): LaneAppointmentLayouts {
  const { maxVisibleColumns = 2 } = options;
  const layouts: LaneAppointmentLayouts = new Map();

  for (const [laneId, laneAppointments] of Object.entries(appointmentsByLane)) {
    const processedAppointments = new Set<string>();

    for (const appointment of laneAppointments) {
      const appointmentId = getAppointmentIdString(appointment);
      if (processedAppointments.has(appointmentId)) continue;

      const overlappingGroup: Appointment[] = [appointment];
      processedAppointments.add(appointmentId);

      // Transitively grow the group with any other lane appointment that
      // overlaps with a current group member.
      let foundNew = true;
      while (foundNew) {
        foundNew = false;
        for (const otherApp of laneAppointments) {
          const otherAppId = getAppointmentIdString(otherApp);
          if (processedAppointments.has(otherAppId)) continue;

          const otherStart = new Date(getEffectiveStart(otherApp));
          const otherEnd = new Date(getEffectiveEnd(otherApp));

          for (const groupApp of overlappingGroup) {
            const groupStart = new Date(getEffectiveStart(groupApp));
            const groupEnd = new Date(getEffectiveEnd(groupApp));

            if (otherStart < groupEnd && otherEnd > groupStart) {
              overlappingGroup.push(otherApp);
              processedAppointments.add(otherAppId);
              foundNew = true;
              break;
            }
          }

          if (foundNew) break;
        }
      }

      if (overlappingGroup.length === 0) continue;

      const groupWithIds = overlappingGroup.map((app) => ({
        ...app,
        startDate: getEffectiveStart(app),
        endDate: getEffectiveEnd(app),
        id: getAppointmentIdString(app),
      }));

      const groupLayouts = calculateAppointmentLayouts(groupWithIds, {
        maxVisibleColumns,
      });

      for (const [layoutId, layout] of groupLayouts.entries()) {
        const appointmentKey = `${laneId}-${layoutId}`;
        layouts.set(appointmentKey, new Map([[layoutId, layout]]));
      }
    }
  }

  return layouts;
}
